'use client';

import { cn } from '@/lib/utils';
import { ArrowDownToLine, Loader } from 'lucide-react';
import React, { ButtonHTMLAttributes } from 'react';
import AnimatedArrow from './AnimatedArrow';

/* eslint-disable */
enum ButtonVariant {
    Primary = 'primary',
    Secondary = 'secondary',
    Upload = 'upload',
    Send = 'send',
}
/* eslint-enable */

interface CustomButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'upload' | 'send';
    children?: React.ReactElement;
    className?: string;
    textClasses?: string;
    title?: string;
    disabled?: boolean;
    submitting?: boolean;
    withArrow?: boolean;
    inverted?: boolean;
}

const Button = ({
    children,
    variant = ButtonVariant.Primary,
    className,
    textClasses,
    type = 'button',
    title,
    disabled = false,
    submitting = false,
    withArrow = false,
    inverted = false,
    ...buttonProps
}: CustomButtonProps) => {
    const isPrimary = variant === ButtonVariant.Primary;
    const isSecondary = variant === ButtonVariant.Secondary;
    const isUpload = variant === ButtonVariant.Upload;
    const isSend = variant === ButtonVariant.Send;

    const buttonClasses = cn(
        'rounded-30 border-primary border-none px-6 py-[10px] focus:outline-none flex justify-center items-center transition-all max-sm:py-[6px] max-sm:px-2 outline-none',
        isPrimary && 'bg-white-glass hover:bg-brand hover:opacity-100 text-white',
        isSend && 'bg-black text-white',
        isSend && !disabled && 'bg-brand text-white',
        (isSecondary || isUpload) &&
            'border bg-transparent border-solid border-dark-grey text-dark-grey hover:bg-brand hover:text-white hover:border-brand',
        (isUpload || isSecondary || isPrimary) && disabled && 'opacity-80',
        withArrow && 'group',
        disabled && 'cursor-default pointer-events-none',
        className,
    );

    const textStyles = cn(
        'font-medium max-sm:text-h6 text-h5',
        isPrimary && 'text-white text-h5 max-sm:text-h6',
        textClasses,
    );

    return (
        <button {...buttonProps} className={buttonClasses} type={type} disabled={disabled}>
            {submitting ? (
                <Loader width={24} height={24} className="animate-spin" />
            ) : (
                <>
                    {inverted && children}
                    {title && <span className={textStyles}>{title}</span>}
                    {!inverted && children}
                    {isUpload && <ArrowDownToLine className="ml-4" width={24} height={24} />}
                    {withArrow && <AnimatedArrow className="ml-5 max-sm:ml-[10px]" />}
                </>
            )}
        </button>
    );
};

export default Button;
